import React, { MouseEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";
import { v4 } from "uuid";

import { IgIconButton, IgTooltip, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceDto,
  OrganizationDto,
  VariationDto,
} from "@intelligems/ig-types";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteExperience } from "../../../api/experience-actions/delete-experience";
import { useDuplicateExperience } from "../../../api/experiences/duplicate-experience";
import { usePatchExperience } from "../../../api/experiences/patch-experience";
import { processesKeys } from "../../../api/queryKeys";
import {
  CAMPAIGN_ROUTES,
  EXPERIMENT_ROUTES,
  PERSONALIZATION_ROUTES,
} from "../../../routes/routes";
import { setDrawer } from "../../../store/slices/global";
import { IAppState } from "../../../store/types/store";
import { FoundExperimentDetails } from "../../../types/experience";
import { ExperienceFe } from "../../../types/schemas/experiences";
import { VariationFe } from "../../../types/schemas/variations";
import { generatePreviewLink } from "../../../utils/generatePreviewLink";
import {
  mpClickDuplicateCampaignButton,
  mpClickEditCampaignButton,
} from "../../../utils/mixpanel";

export interface MoreMenuPersonalizationProps {
  isUsedInTest: FoundExperimentDetails;
  experience: ExperienceDto | ExperienceFe;
  variations: VariationDto[] | VariationFe[];
  organization: OrganizationDto;
  renderLocation: "overview" | "experience";
}

export const MoreMenuPersonalization = ({
  isUsedInTest,
  experience,
  organization,
  variations,
  renderLocation,
}: MoreMenuPersonalizationProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const deleteExperience = useDeleteExperience(
    experience as ExperienceDto,
    "personalization"
  );
  const patchExperience = usePatchExperience(experience?.id);
  const duplicateExperience = useDuplicateExperience("personalization");
  const queryClient = useQueryClient();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onDeleteExperience = (campaignId: string) => {
    deleteExperience.mutate({
      body: { variationId: undefined },
      queryParams: undefined,
      pathParams: {
        experienceId: campaignId,
        action: "delete",
      },
    });

    handleClose();
  };

  return (
    <>
      <IgIconButton
        size={"small"}
        id="more-button"
        aria-controls="more-positioned-menu"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        loading={
          deleteExperience.isPending ||
          patchExperience.isPending ||
          duplicateExperience.isPending
        }
      >
        <MoreVertIcon />
      </IgIconButton>
      <Menu
        id="more-positioned-menu"
        aria-labelledby="more-positioned-button"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        {renderLocation === "overview" && (
          <Link
            style={{ textDecoration: "none" }}
            to={`${PERSONALIZATION_ROUTES.Personalization}/${experience?.id}?action=edit`}
            state={{
              isUsedInActiveTest: isUsedInTest,
              discountId: variations[0].offer?.id,
            }}
            onClick={() => {
              mpClickEditCampaignButton();
            }}
          >
            <MenuItem>
              <IgTypography>View & Edit</IgTypography>
            </MenuItem>
          </Link>
        )}
        {renderLocation === "overview" && (
          <MenuItem
            onClick={() => {
              window.open(
                generatePreviewLink({ organization, experience }),
                "_blank"
              );
            }}
          >
            <IgTypography>Preview</IgTypography>
          </MenuItem>
        )}
        <MenuItem
          disabled={!experience?.id}
          style={{ textDecoration: "none", color: "black" }}
          onClick={() => {
            handleClose();
            queryClient.setQueryData(processesKeys.one(organization.id), {
              processes: [
                {
                  id: v4(),
                  orgId: organization.id,
                  type: "duplicateExperience",
                  severity: "info",
                  title: `Duplicating ${experience?.name}`,
                  isLoading: true,
                },
              ],
            });
            if (experience.id) {
              duplicateExperience.mutate({
                body: undefined,
                pathParams: { experienceId: experience!.id! },
                queryParams: undefined,
              });
            }
            mpClickDuplicateCampaignButton();
          }}
        >
          <IgTypography>Duplicate</IgTypography>
        </MenuItem>
        {isUsedInTest.id && (
          <MenuItem
            onClick={() => {
              navigate(
                `${EXPERIMENT_ROUTES.OfferTest}/${isUsedInTest.id}?action=edit&step=0`
              );
            }}
          >
            <IgTypography>View A/B Test</IgTypography>
          </MenuItem>
        )}
        {renderLocation === "experience" && (
          <MenuItem
            disabled={experience?.id === undefined}
            onClick={() => {
              dispatch(
                setDrawer({
                  drawer: "history",
                  open: true,
                  props: { type: "personalization/offer" },
                })
              );
            }}
          >
            <IgTypography>View History</IgTypography>
          </MenuItem>
        )}
        {renderLocation === "overview" && (
          <IgTooltip
            title={
              isUsedInTest.id
                ? "This personalization is in use by a live or pending offer test"
                : ""
            }
          >
            <MenuItem
              onClick={() => {
                if (experience.id) {
                  onDeleteExperience(experience.id);
                }
              }}
              disabled={
                Boolean(isUsedInTest.id) || experience.status === "started"
              }
            >
              <IgTypography>Delete</IgTypography>
            </MenuItem>
          </IgTooltip>
        )}
        {renderLocation === "experience" && (
          <MenuItem
            onClick={() => navigate(PERSONALIZATION_ROUTES.Personalizations)}
          >
            <IgTypography sx={{ fontSize: "15px" }}>
              Exit Personalization
            </IgTypography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
